import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AuthService } from '../auth/auth.service';
import { DataRequest, DataResponse } from '../../dto/dtos';
import { Observable, timer } from 'rxjs';
import { map, switchMap, filter, take, timeout } from 'rxjs/operators';
import { AxonService } from '../axon-service';
import { HttpDataService } from '../http/http-data.service';
import { Notifier } from '../../utils/notifier';

@Injectable({
  providedIn: 'root'
})
/**
 * Dashboard service for fetching chart data.
 * Implements async job-based API for all charts with polling support.
 */
export class DashBoardService extends AxonService {

    private httpService: HttpDataService;
    private readonly POLL_INTERVAL_MS = 3000;  // Poll every 3 seconds
    private readonly MAX_POLL_WAIT_MS = 5 * 60 * 1000;  // 5 minute timeout

    constructor(_httpClient: HttpClient, _authService: AuthService, _notifier: Notifier) {
        super( _httpClient, _authService, _notifier );
    }

    /**
     * Fetches all 8 dashboard charts using the legacy synchronous endpoint.
     * Kept for backward compatibility.
     */
    public getCharts(): Observable<DataResponse> {
        const dataRequest: DataRequest = {};
        return this.getDataResponse(dataRequest, '/system');
    }

    /**
     * Starts and polls a single chart job asynchronously.
     * Returns the chart data once the query completes.
     * 
     * @param chartKey chart identifier (e.g., "submissions", "system-summary-7")
     * @return Observable that emits the chart data when ready
     */
    public startAndPollChart(chartKey: string): Observable<DataResponse> {
        const dataRequest: DataRequest = {};
        
        // Step 1: Start the job
        return this.startChartJob(chartKey, dataRequest).pipe(
            // Step 2: Poll for result
            switchMap(startResp => {
                if (!startResp.success) {
                    const errorCode = startResp.errorCode !== undefined && startResp.errorCode !== null
                        ? String(startResp.errorCode)
                        : 'UNKNOWN_ERROR';
                    throw new Error(errorCode);
                }
                return this.pollUntilComplete(chartKey, dataRequest);
            })
        );
    }

    /**
     * Polls a chart job until it completes or times out
     */
    private pollUntilComplete(chartKey: string, dataRequest: DataRequest): Observable<DataResponse> {
        // Poll every POLL_INTERVAL_MS until we get data or error
        return timer(0, this.POLL_INTERVAL_MS).pipe(
            switchMap(() => this.pollChartJob(chartKey, dataRequest)),
            // Emit when we have data
            filter(resp => !resp.success || (resp.dataList && resp.dataList.length > 0)),
            // Take only the first emission and complete
            take(1),
            // Overall timeout of 5 minutes
            timeout(this.MAX_POLL_WAIT_MS)
        );
    }

    /**
     * Starts a chart job (returns immediately with job ID)
     */
    private startChartJob(chartKey: string, dataRequest: DataRequest): Observable<DataResponse> {
        return this.getDataResponse(dataRequest, `/${chartKey}/start`);
    }

    /**
     * Polls the status of a chart job
     */
    private pollChartJob(chartKey: string, dataRequest: DataRequest): Observable<DataResponse> {
        return this.getDataResponse(dataRequest, `/job/${chartKey}`);
    }

    /**
     * Helper to get the observable containing the response
     * @param dataRequest
     * @param subpath - the subpath following the rootpath
     * @param rootpath - if null, will be set to /dashboard
     */
    private getDataResponse( dataRequest: DataRequest, subpath: string, rootpath?: string ): Observable<DataResponse> {

        /* If not defined, set to /dashboard i.e. the default root path */
        if ( !rootpath ) {
            rootpath = '/dashboard';
        }

        this.httpService = new HttpDataService(this.httpClient, this.authService, rootpath, this.notifier);
        return this.httpService.sendData(dataRequest, subpath).pipe(
            map(response => {
                return response;
            })
        );
    }
}

